//import clsx from "clsx";
//import { useState } from "react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/scrollbar";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  ChallengeVariant,
} from "../../components/Cards/ChallengeCard";
import ChallengeCard from "../../components/Cards/ChallengeCard";
import Skeleton from "../../components/Common/Skeleton";
import StyledPagination from "../../components/Common/StyledPagination";
import Popup from "../../components/Popup";
import { useGetChallengesQuery } from "../../configs/apis/GetChallenges";
import type { Challenge } from "../../configs/apis/GetChallenges/models";
import { useGetProgressQuery } from "../../configs/apis/GetProgress";
import type { Progress } from "../../configs/apis/GetProgress/models";
import type { MilestonePrize, Prize } from "../../configs/apis/models";
import { useUpdateIsClaimedMutation } from "../../configs/apis/UpdateIsClaimed";
import { getActiveMilestonePrize } from "../../configs/utils/utility";

const PAGE_SIZE = 8;
type AllChallengesPageProps = {
  isInProgress?: boolean;
};

const AllChallengesPage = ({
  isInProgress = false,
}: AllChallengesPageProps) => {
  const { t: translate } = useTranslation("translation");
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupReward, setPopupReward] = useState<
    MilestonePrize | Prize | undefined
  >();
  const [claimableChallengeId, setClaimableChallengeId] = useState<number | undefined>();
  const [currentPage, setCurrentPage] = useState(1);

  const [updateIsClaimed, { isLoading: isClaiming }] = useUpdateIsClaimedMutation();

  const {
    data: forYouData,
    isLoading: isForYouLoading,
    isFetching: isForYouFetching,
    refetch: refetchForYou,
  } = useGetChallengesQuery(
    {
      page: currentPage,
      itemsPerPage: PAGE_SIZE,
      categoryId: undefined,
    },
    { skip: isInProgress },
  );

  const {
    data: inProgressData,
    isLoading: inProgressLoading,
    isFetching: inProgressFetching,
    refetch: refetchInProgress,
  } = useGetProgressQuery(
    {
      categoryId: undefined,
      page: currentPage,
      itemsPerPage: PAGE_SIZE,
    },
    { skip: !isInProgress },
  );

  const challengeData = isInProgress
    ? (inProgressData?.data as Progress[])
    : (forYouData?.data as Challenge[]);

  const getPopupPrize = (reward?: MilestonePrize | Prize): Prize | undefined => {
    if (!reward) return undefined;
    return "prizes" in reward ? reward.prizes?.at(0) : reward;
  };

  const getVariant = (card: Challenge): ChallengeVariant => {
    if (isInProgress) {
      if (card?.prizes.length !== 0) return "claimable";
      return "inProgress";
    }

    if (card.isLocked) return "locked";
    return "active";
  };

  return (
    <>
      <Popup
        isVisible={isPopupVisible}
        onClose={() => {
          setIsPopupVisible(false);
          setPopupReward(undefined);

          if (claimableChallengeId) {
            updateIsClaimed({
              challengeId: claimableChallengeId,
            })
              .unwrap()
              .then(() => {
                if (isInProgress) {
                  refetchInProgress();
                } else {
                  refetchForYou();
                }
              })
              .catch(() => {})
              .finally(() => {
                setClaimableChallengeId(undefined);
              });
          }
        }}
        prize={getPopupPrize(popupReward)}
      />
      <header className="z-10 mb-8 pt-4">
        <div className="mb-8 flex items-center justify-start">
          <h1 className="pointer-events-none text-center text-4xl font-semibold">
            {isInProgress ? translate("in_progress") : translate("challenges_pager")}
          </h1>
        </div>
      </header>
      <main className="mt-12">
        <div className="grid grid-cols-1 gap-5 px-5 md:grid-cols-2 lg:grid-cols-4">
          {(isForYouLoading ||
            isForYouFetching ||
            inProgressLoading ||
            inProgressFetching) ? (
              Array.from({ length: PAGE_SIZE }).map((_, idx) => <Skeleton key={idx} />)
            ) : (
              challengeData?.map((card) => {
                const activePrize = isInProgress
                  ? getActiveMilestonePrize((card as Progress).prizes)
                  : undefined;

                return (
                  <ChallengeCard
                    key={card.id}
                    {...card}
                    categoryName={card.category?.name ?? ""}
                    isClaimingThis={isClaiming && claimableChallengeId === card.id}
                    onClaim={() => {
                      setClaimableChallengeId(card.id);
                      setPopupReward(isInProgress ? activePrize : card.prizes[0]);
                      setIsPopupVisible(true);
                    }}
                    prize={activePrize}
                    progress={
                      isInProgress
                        ? (card as Progress)?.averageProgress
                        : undefined
                    }
                    skipImmediateClaim={true}
                    variant={getVariant(card)}
                  />
                );
              })
            )}
        </div>
        {challengeData && challengeData.length > 0 && (
          <div className="mt-8 flex justify-center">
            <StyledPagination
              current={currentPage}
              onChange={(newPage) => setCurrentPage(newPage)}
              pageSize={PAGE_SIZE}
              total={
                isInProgress
                  ? (inProgressData?.count ?? 0)
                  : (forYouData?.count ?? 0)
              }
            />
          </div>
        )}
      </main>
    </>
  );
};

export default AllChallengesPage;
